/**
 * 영상 자막(손님이 남기는 한 줄) + 후처리 출력 기하 (BOOTH-85, 캔버스 9.3 "한 줄을 남기시겠어요?").
 *
 * 글자는 **키오스크가 캔버스로 그려 투명 PNG** 로 올리고(키오스크 서체 그대로, 4개 언어 자동), 서버 ffmpeg 는 얹기만 한다.
 * 결과 프레임: 공급자 출력(Veo 9:16 · xAI 3:4) → 폭 기준 가운데 3:4 크롭(`VIDEO_PHOTO_W×H`) → 자막이 있으면 **사진 하단 1/4 영역 안에**
 * 겹친다(`CAPTION_OVERLAY_*`, 투명→잉크 그늘 + 종이색 글자 + 페이드인) — 결과는 자막 유무와 무관하게 항상 3:4.
 * 처음(2026-10-16)엔 아래 종이색 띠를 붙여 2:3 으로 냈으나 "글자를 붙인 카드" 로 보여 2026-10-19 영상 안 겹침으로 바꿨다 — `"2:3"` 은
 * 그때 만든 레코드 표시용으로만 남는다. 키오스크 미리보기·콘솔·공유 페이지·서버가 같은 상수를 쓴다.
 */
/** 자막 최대 길이 — grapheme 기준(이모지·결합 문자 1개 = 1자). 캔버스 9.3 "최대 12자". */
export const CAPTION_MAX_GRAPHEMES = 12;
/** 자막 PNG dataURL 상한(디코드 바이트). 1440×240 투명 PNG 에 글자 12자는 보통 30KB 안쪽. */
export const CAPTION_IMAGE_MAX_BYTES = 512 * 1024;
/** 후처리 출력 — 사진 영역 (3:4). */
export const VIDEO_PHOTO_W = 720;
export const VIDEO_PHOTO_H = 960;
/** 자막 오버레이 영역 — 사진 하단 1/4(720×240). 서버는 PNG 를 이 크기로 축소해 `y = CAPTION_OVERLAY_Y` 에 겹친다. */
export const CAPTION_OVERLAY_H = 240;
export const CAPTION_OVERLAY_Y = VIDEO_PHOTO_H - CAPTION_OVERLAY_H;
/** 키오스크가 그리는 자막 PNG 크기 — 영역의 2배(레티나). 그늘 그라데이션 + 글자가 함께 들어 있어 서버는 overlay 만 한다. */
export const CAPTION_PNG_W = VIDEO_PHOTO_W * 2;
export const CAPTION_PNG_H = CAPTION_OVERLAY_H * 2;
/** 글자 중심의 세로 위치(영역 높이 대비). 아래쪽에 두어 얼굴·상체를 가리지 않는다. */
export const CAPTION_TEXT_Y_RATIO = 0.62;
/** 영역 위(투명) → 아래(잉크) 그늘의 최대 불투명도 — 밝은 배경에서도 종이색 글자가 읽히게. */
export const CAPTION_SHADE_ALPHA = 0.55;
/** HERIT 종이색 — 자막 글자 색(영상 위에 올리므로 밝은 쪽). */
export const VIDEO_PAPER_COLOR = "#F6F3ED";
/** HERIT 잉크 — 자막 그늘 색(`captionShadeColor`). */
export const VIDEO_INK_COLOR = "#171411";
/** 잉크색을 주어진 불투명도의 `rgba()` 로 — 캔버스 그라데이션·CSS 미리보기 공용. */
export function captionShadeColor(alpha) {
    const hex = VIDEO_INK_COLOR.replace(/^#/, "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/**
 * 후처리 결과 비율 — 자막이 있어도 3:4(영상 안에 겹침, 2026-10-19). 인자는 호환용으로 남겼다.
 * `"2:3"` 은 2026-10-16~19 사이 띠 방식으로 만든 레코드가 가진 값으로, 뷰가 그 비율로 그려 주기만 한다.
 */
export function videoOutputAspect(_hasCaption) {
    return "3:4";
}
/** 뷰가 쓸 프레임 비율. 없음(구 레코드·후처리 실패) = 3:4 — 9:16 원본을 cover 로 가린다. */
export function videoAspectRatio(aspect) {
    return aspect === "2:3" ? { w: 2, h: 3 } : { w: 3, h: 4 };
}
/** CSS `aspect-ratio` 값. */
export function videoAspectCss(aspect) {
    const r = videoAspectRatio(aspect);
    return `${r.w} / ${r.h}`;
}
/** 입력 정규화 — 앞뒤 공백 제거, 개행·연속 공백은 한 칸으로. 한 줄만 받는다. */
export function normalizeCaption(raw) {
    return raw.replace(/\s+/g, " ").trim();
}
/** grapheme 수 — `Intl.Segmenter` 가 있으면 그걸로(iPad Safari 14.1+), 없으면 코드포인트 수. */
export function captionLength(text) {
    const Seg = Intl.Segmenter;
    if (Seg) {
        let n = 0;
        for (const _ of new Seg(undefined, { granularity: "grapheme" }).segment(text))
            n++;
        return n;
    }
    return Array.from(text).length;
}
/** 자막을 `max` grapheme 까지 잘라낸다 — 입력칸 onChange 가 쓴다. */
export function truncateCaption(text, max = CAPTION_MAX_GRAPHEMES) {
    const Seg = Intl.Segmenter;
    if (Seg) {
        let out = "";
        let n = 0;
        for (const g of new Seg(undefined, { granularity: "grapheme" }).segment(text)) {
            if (n >= max)
                break;
            out += g.segment;
            n++;
        }
        return out;
    }
    return Array.from(text).slice(0, max).join("");
}
/** 정규화된 자막이 1~`CAPTION_MAX_GRAPHEMES` 자인가. */
export function isValidCaption(text) {
    const n = captionLength(text);
    return n >= 1 && n <= CAPTION_MAX_GRAPHEMES && text === normalizeCaption(text);
}
/**
 * 부스의 자막 단계 스위치 (BOOTH-85 후속) — `Booth.captionEnabled` 없음 = 켬(도입 전 동작 유지).
 * 끔이면 키오스크가 자막 화면(9.3)을 건너뛴다. 키오스크 `videoFlow`·콘솔 부스 설정이 같은 판정을 쓴다.
 */
export function resolveCaptionEnabled(booth) {
    return booth?.captionEnabled ?? true;
}
//# sourceMappingURL=video-caption.js.map